import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Package, RefreshCw, AlertCircle, CheckCircle, Clock, ArrowRight } from 'lucide-react';
import { useHistory } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { showNotification, showError } from '../../utils/notificationHandler';
import { format } from 'date-fns';

const BackorderManagementCard = () => {
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [summary, setSummary] = useState({
    totalOrders: 0,
    totalPendingItems: 0,
    totalFulfilledItems: 0,
    totalPartiallyFulfilledItems: 0,
    lastSyncAt: null
  });

  useEffect(() => {
    fetchBackorderSummary();
  }, []);

  const fetchBackorderSummary = async () => {
    try {
      setLoading(true);
      
      // Call the database function to get summary
      const { data, error } = await supabase.rpc('get_backorder_summary');
      
      if (error) throw error;
      
      setSummary(data || {
        totalOrders: 0,
        totalPendingItems: 0,
        totalFulfilledItems: 0,
        totalPartiallyFulfilledItems: 0,
        lastSyncAt: null
      });
    } catch (error) {
      console.error('Error fetching backorder summary:', error);
      showError(error, 'Failed to load backorder summary');
    } finally {
      setLoading(false);
    }
  };

  const handleManualSync = async () => {
    try {
      setSyncing(true);
      showNotification('Starting backorder sync...', 'info');

      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Your session has expired. Please sign in again.');
      }

      // Call the FastAPI sync endpoint (replaces the retired
      // sync-backorder-status Supabase edge function)
      const response = await fetch('/api/v1/sync/backorders', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        throw new Error(
          `Sync endpoint returned an unexpected response (${response.status}). ` +
          'The API may be unreachable or misconfigured.'
        );
      }

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || 'Sync request failed');
      }

      if (data.success) {
        showNotification(
          `Sync complete: ${data.items_updated} items updated out of ${data.items_checked} checked`,
          'success'
        );

        // Refresh summary after sync
        await fetchBackorderSummary();
      } else {
        showError(
          new Error(data.errors?.join(', ') || 'Sync failed'),
          'Sync completed with errors'
        );
      }
    } catch (error) {
      console.error('Error syncing backorders:', error);
      showError(error, 'Failed to sync backorders');
    } finally {
      setSyncing(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'text-yellow-600 bg-yellow-50';
      case 'partially_fulfilled':
        return 'text-orange-600 bg-orange-50';
      case 'fulfilled':
        return 'text-green-600 bg-green-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const formatLastSync = (timestamp) => {
    if (!timestamp) return 'Never synced';
    
    try {
      return format(new Date(timestamp), 'PPp');
    } catch (error) {
      return 'Invalid date';
    }
  };

  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
      >
        <div className="p-6">
          <div className="flex items-center justify-center">
            <RefreshCw className="h-6 w-6 text-gray-400 animate-spin" />
            <span className="ml-3 text-gray-600">Loading backorder data...</span>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow overflow-hidden cursor-pointer group"
      onClick={() => history.push('/admin/backorders')}
    >
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
            <Package className="h-4 w-4 text-gray-500" aria-hidden="true" />
            Backorders
          </h3>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleManualSync();
            }}
            disabled={syncing}
            className="p-2 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors disabled:opacity-50"
            title="Sync backorder status from OrderBoard"
            aria-label="Sync backorder status from OrderBoard"
          >
            <RefreshCw className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Card Content */}
      <div className="p-6">
        {/* Summary Stats Grid */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          {/* Total Orders */}
          <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-blue-600 font-medium mb-1">Total Orders</p>
                <p className="text-2xl font-bold text-blue-900">{summary.totalOrders}</p>
              </div>
              <Package className="h-8 w-8 text-blue-400" />
            </div>
          </div>

          {/* Pending Items */}
          <div className="bg-yellow-50 rounded-lg p-3 border border-yellow-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-yellow-600 font-medium mb-1">Pending</p>
                <p className="text-2xl font-bold text-yellow-900">{summary.totalPendingItems}</p>
              </div>
              <Clock className="h-8 w-8 text-yellow-400" />
            </div>
          </div>

          {/* Partially Fulfilled */}
          <div className="bg-orange-50 rounded-lg p-3 border border-orange-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-orange-600 font-medium mb-1">Partial</p>
                <p className="text-2xl font-bold text-orange-900">{summary.totalPartiallyFulfilledItems}</p>
              </div>
              <AlertCircle className="h-8 w-8 text-orange-400" />
            </div>
          </div>

          {/* Fulfilled Items */}
          <div className="bg-green-50 rounded-lg p-3 border border-green-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-green-600 font-medium mb-1">Fulfilled</p>
                <p className="text-2xl font-bold text-green-900">{summary.totalFulfilledItems}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-400" />
            </div>
          </div>
        </div>

        {/* Last Sync Info */}
        <div className="border-t border-gray-200 pt-4">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center text-gray-600">
              <Clock className="h-4 w-4 mr-2" />
              <span>Last sync: {formatLastSync(summary.lastSyncAt)}</span>
            </div>
            <div className="flex items-center text-orange-600 font-medium group-hover:text-orange-700 transition-colors">
              <span>View Details</span>
              <ArrowRight className="h-4 w-4 ml-1 transform group-hover:translate-x-1 transition-transform" />
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-4 flex space-x-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              history.push('/admin/backorders?status=pending');
            }}
            className="flex-1 px-3 py-2 bg-yellow-100 text-yellow-700 rounded-lg hover:bg-yellow-200 transition-colors text-sm font-medium"
          >
            View Pending
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              history.push('/admin/backorders?status=partially_fulfilled');
            }}
            className="flex-1 px-3 py-2 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 transition-colors text-sm font-medium"
          >
            View Partial
          </button>
        </div>

        {/* Hover Indicator */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex items-center text-xs text-gray-500 group-hover:text-gray-700 transition-colors">
            <span>Click to access full backorder management</span>
            <svg className="ml-1 h-3 w-3 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default BackorderManagementCard;
